import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type Tone = 'neutral' | 'accent' | 'danger' | 'warning' | 'success' | 'info';

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-fg-2 border-line',
  accent: 'bg-accent-soft text-accent-fg border-accent-soft-2',
  danger: 'bg-danger-soft text-danger-fg border-danger-line',
  warning: 'bg-warning-soft text-warning-fg border-warning-line',
  success: 'bg-success-soft text-success-fg border-success-line',
  info: 'bg-info-soft text-info-fg border-info-line',
};

/** Значок стану: прямокутний (4 px), як штамп у реєстрі. Колір — лише семантичний. */
export function Badge({
  tone = 'neutral',
  icon,
  children,
  className,
  title,
  alert,
}: {
  tone?: Tone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  title?: string;
  /** Попередження, яке не можна загубити: у вузькому рядку переліку лишається значком (див. ALERT_LABEL у TaskBits). */
  alert?: boolean;
}) {
  return (
    <span
      title={title}
      data-alert={alert ? '' : undefined}
      className={cn(
        'inline-flex h-5 max-w-full shrink-0 items-center gap-1 rounded-xs border px-1.5 text-caption font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

/** Лічильник (кругла пігулка): кількість непрочитаних, задач у групі. */
export function Counter({ value, tone = 'neutral', className, label }: { value: number; tone?: 'neutral' | 'accent' | 'danger'; className?: string; label?: string }) {
  return (
    <span
      aria-label={label}
      className={cn(
        'tnum inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-caption font-medium',
        tone === 'neutral' && 'bg-surface-3 text-fg-2',
        tone === 'accent' && 'bg-accent text-on-accent',
        tone === 'danger' && 'bg-danger-soft text-danger-fg',
        className,
      )}
    >
      {value > 999 ? '999+' : value}
    </span>
  );
}

/** Клавіша: звичайна, на темному тлі підказки або на акцентній кнопці. */
export function Kbd({ children, className, tone = 'default' }: { children: ReactNode; className?: string; tone?: 'default' | 'inverse' | 'accent' }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-xs border px-1 text-caption',
        tone === 'default' && 'border-line-strong bg-surface-2 text-fg-2',
        tone === 'inverse' && 'h-4 border-transparent bg-fg-2 text-fg-inverse',
        tone === 'accent' && 'h-4 min-w-4 border-on-accent/30 bg-transparent text-on-accent',
        className,
      )}
    >
      {children}
    </kbd>
  );
}
